import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { UserAuthService } from '@shared/modules/auth/auth-data-access/user-auth.service';
import { map, Observable, of, switchMap, take, tap } from 'rxjs';

@Injectable()
export class AuthGuard {
  private readonly userAuthService: UserAuthService = inject(UserAuthService);
  private readonly router: Router = inject(Router);

  canActivate(): Observable<boolean> {
    return this.userAuthService.isInitialized$.pipe(
      switchMap(() =>
        this.userAuthService.currentSession$.pipe(
          take(1),
          map(session => session?.user?.id ?? null)
        )
      ),
      switchMap(userId => {
        if (!userId) {
          this.router.navigate(['/login']);
          return of(false);
        }

        return this.userAuthService.isUserLoggedIn$(userId).pipe(
          switchMap(isLoggedIn => {
            if (!isLoggedIn) {
              return this.userAuthService.logout$(userId).pipe(
                tap(() => this.router.navigate(['/login'])),
                map(() => false)
              );
            }

            return of(true);
          })
        );
      })
    );
  }
}

export const AuthGuardFn: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  return inject(AuthGuard).canActivate();
};
